// The ground view's enemy band — the layer that makes the board's guards
// *act* (phase D of the AOD Studio arc). The spawn is deterministic from
// a seed key (the same post always musters the same band on the same
// tiles), the per-tick update chains the two ported brains — GOAP picks
// the goal band (strike / close in / hold the post), the tactical
// `decide` picks the target, ability and movement — and the walk is
// clamped by the same passability rule the player's `walkStep` obeys.
//
// Nothing here writes to the sim: an enemy's strike is *presented*, not
// resolved. Strike events are handed to the `onStrike` callback so the
// combat wiring (phase 8's "the sim decides" half) can consume them
// without this module knowing it exists.

import { worldToAxial } from '../hexwalk.js';
import {
  type AiBehavior,
  type AiDecision,
  type ArenaTarget,
  type GuardClass,
  decide,
  kitFor,
} from './arenaAI.js';
import { selectAbility } from './arenaAI.js';
import {
  type GoapAction,
  type GoapAgentState,
  type GoapGoal,
  type GroundViewWorld,
  SIGHT_RANGE,
  agentStep,
  createAgent,
} from './goap.js';
import type { Rng } from './rng.js';

/** One guard: the tactical unit's state plus its GOAP agent. */
export interface EnemyCombatant {
  readonly id: string;
  readonly guardClass: GuardClass;
  readonly behavior: AiBehavior;
  x: number;
  z: number;
  heading: number;
  hp: number;
  readonly maxHp: number;
  cooldownRemaining: number;
  /** Wind-up animation left on the current strike (0 = none). */
  strikeRemaining: number;
  readonly postX: number;
  readonly postZ: number;
  agent: GoapAgentState;
}

export interface EnemyStrikeEvent {
  readonly enemyId: string;
  readonly guardClass: GuardClass;
  readonly abilityId: string;
  readonly atSeconds: number;
}

export interface EnemyBand {
  readonly seedKey: string;
  enemies: EnemyCombatant[];
  nowSeconds: number;
}

/** The band's fixed composition: two melee ranks and an archer. */
const COMPOSITION: readonly GuardClass[] = ['grunt', 'grunt', 'archer'];
const SPAWN_MIN_DISTANCE = 2;
const SPAWN_MAX_DISTANCE = 4;
const MAX_HP = 30;

export interface SpawnTileSource {
  /** Axial coords of passable tiles (the caller filters from `tiles`). */
  readonly passable: readonly { readonly q: number; readonly r: number }[];
}

/**
 * Muster a guard band near a world position. Tiles are taken in ring
 * order from the spawn hex (deterministic, no shuffle): the closest
 * passable hexes at walking distance 2–4 become the guards' posts. Fewer
 * candidates than guards simply musters a smaller band.
 */
export function spawnEnemyBand(
  seedKey: string,
  spawn: { readonly x: number; readonly z: number },
  passable: readonly { readonly q: number; readonly r: number }[],
): EnemyBand {
  const origin = worldToAxial(spawn.x, spawn.z);
  const candidates = passable
    .map((t) => ({ t, d: hexDistance(origin.q, origin.r, t.q, t.r) }))
    .filter(({ d }) => d >= SPAWN_MIN_DISTANCE && d <= SPAWN_MAX_DISTANCE)
    .sort((a, b) => a.d - b.d || a.t.q - b.t.q || a.t.r - b.t.r)
    .map(({ t }) => t);

  const enemies: EnemyCombatant[] = [];
  for (let i = 0; i < COMPOSITION.length && i < candidates.length; i++) {
    const post = candidates[i]!;
    const [px, pz] = axialToWorld(post.q, post.r);
    const guardClass = COMPOSITION[i]!;
    const id = `${seedKey}:${guardClass}:${post.q},${post.r}`;
    enemies.push({
      id,
      guardClass,
      behavior: guardClass === 'archer' ? 'defensive' : 'aggressive',
      x: px,
      z: pz,
      heading: 0,
      hp: MAX_HP,
      maxHp: MAX_HP,
      cooldownRemaining: 0,
      strikeRemaining: 0,
      postX: px,
      postZ: pz,
      agent: createAgent(goalsFor(), actionsFor(guardClass)),
    });
  }
  // Placement is ring-ordered, so the seed key alone determines the band.
  return { seedKey, enemies, nowSeconds: 0 };
}

/**
 * One update tick. `dt` in seconds; `player` is the FP walker. Returns
 * the strikes begun this tick. Decisions re-run every tick — the tactical
 * decide is cheap and the GOAP agent only plans when its plan runs dry.
 */
export function updateEnemyBand(
  band: EnemyBand,
  player: { readonly x: number; readonly z: number },
  dt: number,
  passableAt: (q: number, r: number) => boolean,
): EnemyStrikeEvent[] {
  band.nowSeconds += dt;
  const strikes: EnemyStrikeEvent[] = [];
  const target: ArenaTarget = {
    id: 'player',
    position: [player.x, 0, player.z],
    hp: 100,
    maxHp: 100,
    isAlive: true,
  };

  for (const enemy of band.enemies) {
    if (enemy.hp <= 0) continue;
    enemy.cooldownRemaining = Math.max(0, enemy.cooldownRemaining - dt);
    enemy.strikeRemaining = Math.max(0, enemy.strikeRemaining - dt);

    const world: GroundViewWorld = {
      enemy: {
        x: enemy.x,
        z: enemy.z,
        hp: enemy.hp,
        maxHp: enemy.maxHp,
        postX: enemy.postX,
        postZ: enemy.postZ,
        abilityCooldownRemaining: enemy.cooldownRemaining,
        attackRange: kitFor(enemy.guardClass).special.range,
      },
      player: { x: player.x, z: player.z, hp: target.hp },
      nowSeconds: band.nowSeconds,
    };

    // The GOAP layer's strike action owns the wind-up; everything else
    // defers to the tactical decide for movement. The ability that
    // actually fires is the tactical layer's choice (special when it is
    // ready and reaches, else the basic).
    const begun = agentStep(enemy.agent, world, dt);
    if (begun && begun.name === 'strike') {
      const kit = kitFor(enemy.guardClass);
      const ability = selectAbility(toTactical(enemy), target) ?? kit.basic;
      enemy.strikeRemaining = ability.windUpSeconds;
      enemy.cooldownRemaining = kit.cooldownSeconds;
      strikes.push({
        enemyId: enemy.id,
        guardClass: enemy.guardClass,
        abilityId: ability.id,
        atSeconds: band.nowSeconds,
      });
    }

    if (enemy.strikeRemaining > 0) continue; // planted while swinging

    // The ported decide always chases its target — it has no sight
    // concept. The sight gate is this layer's job: beyond SIGHT_RANGE a
    // guard disengages and walks back to its post.
    const dist = Math.hypot(enemy.x - player.x, enemy.z - player.z);
    const decision: AiDecision =
      dist <= SIGHT_RANGE
        ? decide(toTactical(enemy), [target], NEVER_RANDOM)
        : { moveTarget: [enemy.postX, 0, enemy.postZ], abilityId: null, targetId: null };
    enemy.heading = headingToward(enemy, player, decision);
    if (decision.moveTarget) {
      stepWithClamp(enemy, decision.moveTarget, dt, passableAt);
    }
  }
  return strikes;
}

function toTactical(enemy: EnemyCombatant) {
  return {
    id: enemy.id,
    guardClass: enemy.guardClass,
    behavior: enemy.behavior,
    position: [enemy.x, 0, enemy.z] as [number, number, number],
    hp: enemy.hp,
    maxHp: enemy.maxHp,
    isAlive: enemy.hp > 0,
    cooldownRemaining: enemy.cooldownRemaining,
  };
}

/** Band enemies choose targets by behavior; the rng path stays unused
 *  (and deterministic) in play — tests drive it directly. */
const NEVER_RANDOM: Rng = () => 0;

function headingToward(
  enemy: EnemyCombatant,
  player: { readonly x: number; readonly z: number },
  decision: AiDecision,
): number {
  // Face the player while engaged; face the walk direction when roaming.
  const dist = Math.hypot(enemy.x - player.x, enemy.z - player.z);
  const [tx, tz] =
    dist <= SIGHT_RANGE
      ? [player.x, player.z]
      : decision.moveTarget != null
        ? [decision.moveTarget[0], decision.moveTarget[2]]
        : [enemy.postX, enemy.postZ];
  // three.js: local +Z points at (sin θ, cos θ), so facing the target is
  // atan2(dx, dz) — the player figure's `yaw + π` is the same relation.
  return Math.atan2(tx - enemy.x, tz - enemy.z);
}

/**
 * Continuous step toward a move target, clamped exactly like the
 * player's walk: entering an impassable hex (or off-board) is refused —
 * the guard stops at the edge rather than crossing water.
 */
function stepWithClamp(
  enemy: EnemyCombatant,
  moveTarget: readonly [number, number, number],
  dt: number,
  passableAt: (q: number, r: number) => boolean,
): void {
  const dx = moveTarget[0] - enemy.x;
  const dz = moveTarget[2] - enemy.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-4) return;
  const speed = 1.6;
  const step = Math.min(len, speed * dt);
  const nx = enemy.x + (dx / len) * step;
  const nz = enemy.z + (dz / len) * step;
  const { q, r } = worldToAxial(nx, nz);
  if (!passableAt(q, r)) return;
  enemy.x = nx;
  enemy.z = nz;
}

// ── The GOAP vocabulary ─────────────────────────────────────────────

function goalsFor(): readonly GoapGoal[] {
  return [
    { name: 'strike', priority: 3, desiredState: { playerStruck: true } },
    { name: 'engage', priority: 2, desiredState: { playerInReach: true } },
    { name: 'holdPost', priority: 1, desiredState: { onPost: true } },
  ];
}

function actionsFor(guardClass: GuardClass): readonly GoapAction[] {
  const kit = kitFor(guardClass);
  return [
    {
      name: 'strike',
      cost: 1,
      preconditions: { playerInReach: true, abilityReady: true },
      effects: { playerStruck: true },
      durationSeconds: kit.basic.windUpSeconds,
      proceduralPrecondition: (w) =>
        Math.hypot(w.enemy.x - w.player.x, w.enemy.z - w.player.z) <= w.enemy.attackRange &&
        w.enemy.abilityCooldownRemaining <= 0,
    },
    {
      name: 'closeIn',
      cost: 2,
      preconditions: { playerVisible: true },
      effects: { playerInReach: true },
      durationSeconds: 0.1,
      proceduralPrecondition: (w) =>
        Math.hypot(w.enemy.x - w.player.x, w.enemy.z - w.player.z) <= SIGHT_RANGE,
    },
    {
      name: 'returnToPost',
      cost: 3,
      preconditions: {},
      effects: { onPost: true },
      durationSeconds: 0.1,
    },
  ];
}

// Local hex ring distance (axial), mirroring HexGrid's cube rounding.
export function hexDistance(q1: number, r1: number, q2: number, r2: number): number {
  const dq = q1 - q2;
  const dr = r1 - r2;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

/** axial→world, identical to hexwalk's (kept local to avoid a cycle-prone import). */
function axialToWorld(q: number, r: number): [number, number] {
  return [Math.sqrt(3) * (q + r / 2), 1.5 * r];
}

export { SIGHT_RANGE };
